export const formatPhoneNumber = (value: string) => {
	const onlyNums = value.replace(/\D/g, '');
	let formatted = '';

	if (onlyNums.startsWith('02')) {
		// 서울 지역번호 (2자리)
		if (onlyNums.length <= 2) {
			formatted = onlyNums;
		} else if (onlyNums.length <= 5) {
			formatted = `${onlyNums.slice(0, 2)}-${onlyNums.slice(2)}`;
		} else if (onlyNums.length <= 9) {
			formatted = `${onlyNums.slice(0, 2)}-${onlyNums.slice(2, 5)}-${onlyNums.slice(5)}`;
		} else {
			formatted = `${onlyNums.slice(0, 2)}-${onlyNums.slice(2, 6)}-${onlyNums.slice(6, 10)}`;
		}
	} else {
		// 나머지 지역번호 또는 휴대폰번호 (3자리)
		if (onlyNums.length <= 3) {
			formatted = onlyNums;
		} else if (onlyNums.length <= 6) {
			formatted = `${onlyNums.slice(0, 3)}-${onlyNums.slice(3)}`;
		} else if (onlyNums.length <= 10) {
			formatted = `${onlyNums.slice(0, 3)}-${onlyNums.slice(3, 6)}-${onlyNums.slice(6)}`;
		} else {
			formatted = `${onlyNums.slice(0, 3)}-${onlyNums.slice(3, 7)}-${onlyNums.slice(7, 11)}`;
		}
	}

	return formatted;
};

const getDatePartsInKST = (value: Date | string | null | undefined) => {
	if (!value) return null;

	const date = new Date(value);
	if (isNaN(date.getTime())) {
		return null;
	}

	const options: Intl.DateTimeFormatOptions = {
		timeZone: 'Asia/Seoul',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hour12: false
	};

	const formatter = new Intl.DateTimeFormat('ko-KR', options);
	const parts = formatter.formatToParts(date);

	const partsMap = parts.reduce((acc, part) => {
		if (part.type !== 'literal' && part.type !== 'weekday') {
			acc[part.type] = part.value;
		}
		return acc;
	}, {} as Record<string, string>);
	
	if (partsMap.hour === '24') {
		partsMap.hour = '00';
	}

	return partsMap;
}

export const formatDate = (value: Date | string | null | undefined) => {
	const parts = getDatePartsInKST(value);
	if (!parts) return '';
	return `${parts.year}-${parts.month}-${parts.day}`;
}

export const formatDateYYYYMMDD = (value: Date | string | null | undefined) => {
	const parts = getDatePartsInKST(value);
	if (!parts) return '';
	return `${parts.year}${parts.month}${parts.day}`;
}

export const formatDateWithTime = (value: Date | string | null | undefined) => {
	const parts = getDatePartsInKST(value);
	if (!parts) return '';
	return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
}

/**
 * Java LocalDateTime 형식 (YYYY-MM-DDTHH:mm:ss)으로 변환
 * UTC 'Z' 없이 로컬 시간대 기준으로 반환합니다.
 */
export const toLocalDateTime = (value: Date | string | null | undefined) => {
	const parts = getDatePartsInKST(value);
	if (!parts) return '';
	return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
}

/** Java ZonedDateTime에서 파싱 가능한 ISO-8601 문자열로 변환 */
export const toZonedDateTime = (value: Date | string | null | undefined) => {
	if (!value) return '';
	const date = new Date(value);
	return isNaN(date.getTime()) ? '' : date.toISOString();
}

export const formatDateYear2 = (value: Date | string | null | undefined) => {
	const parts = getDatePartsInKST(value);
	if (!parts) return '';
	return parts.year.slice(-2);
}

export const formatDateYear4 = (value: Date | string | null | undefined) => {
	const parts = getDatePartsInKST(value);
	if (!parts) return '';
	return parts.year;
}

export const formatDateMonth = (value: Date | string | null | undefined) => {
	const parts = getDatePartsInKST(value);
	if (!parts) return '';
	return parts.month;
}

export const formatDateDay = (value: Date | string | null | undefined) => {
	const parts = getDatePartsInKST(value);
	if (!parts) return '';
	return parts.day;
}

export const formatDateHour = (value: Date | string | null | undefined) => {
	const parts = getDatePartsInKST(value);
	if (!parts) return '';
	return parts.hour;
}

export const formatDateMinute = (value: Date | string | null | undefined) => {
	const parts = getDatePartsInKST(value);
	if (!parts) return '';
	return parts.minute;
}

export const formatDateSecond = (value: Date | string | null | undefined) => {
	const parts = getDatePartsInKST(value);
	if (!parts) return '';
	return parts.second;
}

export const formatDateYYYYMM = (value: Date | string | null | undefined) => {
	const parts = getDatePartsInKST(value);
	if (!parts) return '';
	return `${parts.year}${parts.month}`;
}

export const formatRegNo = (value: string) => {
	// regNo 필드일 경우 포맷 적용
	// 숫자만 남기기
	const onlyNums = value.replace(/\D/g, '');
	let formatted = '';
	// 사업자등록번호 형식으로 포맷팅 (###-##-#####)
	if (onlyNums.length <= 3) {
		formatted = onlyNums;
	} else if (onlyNums.length <= 5) {
		formatted = `${onlyNums.slice(0, 3)}-${onlyNums.slice(3)}`;
	} else if (onlyNums.length <= 10) {
		formatted = `${onlyNums.slice(0, 3)}-${onlyNums.slice(3, 5)}-${onlyNums.slice(5)}`;
	} else {
		formatted = `${onlyNums.slice(0, 3)}-${onlyNums.slice(3, 5)}-${onlyNums.slice(5, 10)}`;
	}

	return formatted;
}

export const stringComparator = (valueA: string, valueB: string, nodeA: any, nodeB: any, isInverted: boolean) => {

	const a = (valueA ?? "").toString().trim();
	const b = (valueB ?? "").toString().trim();

	const aIsEmpty = a === "";
	const bIsEmpty = b === "";

	if (aIsEmpty && bIsEmpty) return 0;
	if (aIsEmpty) return isInverted ? -1 : 1;
	if (bIsEmpty) return isInverted ? 1 : -1;

	return a.localeCompare(b);
};

export const validateString = (value: string) => {
	value = value?.toString().trim() || '';
	if(value === "" || value === null || value === undefined || value === "null" || value === "undefined") {
		return false;
	}
	return true;	
}

export const validateTitleSize = (titleSize: string) => {
	if(titleSize === "sm") {
		return "form-title";
	} else if(titleSize === "md") {
		return "md-form-title";
	} else if(titleSize === "lg") {
		return "lg-form-title";
	} else if(titleSize === "xl") {
		return "xl-form-title";
	} else {
		return "md-form-title";
	}
}

export const validateInputSize = (inputSize: string) => {
	if(inputSize === "ti") {
		return "w-[50px]";
	} else if(inputSize === "sm") {
		return "w-[100px]";
	} else if(inputSize === "md") {
		return "w-[200px]";
	} else if(inputSize === "lg") {
		return "w-[400px]";
	} else if(inputSize === "xl") {
		return "w-[500px]";
	} else if(inputSize === "full") {
		return "w-full";
	} else {
		return "w-[200px]";
	}
}
